import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Room } from 'colyseus.js';
import { parseObjectData } from '../game/object-events.js';
import { useSessionStore } from '../state/session-store.js';

interface DoorView {
  readonly holders: number;
  readonly required: number;
  readonly open: boolean;
}

type SyncedObjects = Map<string, { data?: string; occupants?: ArrayLike<string> }>;

/** The joint progress of the co-op door in reach: how many of the players it needs are holding it. */
export function CoopDoorProgress({ room }: { room: Room }) {
  const { t } = useTranslation();
  const target = useSessionStore((s) => s.interactionTarget);
  const objectId = target?.objectType === 'coop_door' ? target.objectId : undefined;
  const [view, setView] = useState<DoorView | undefined>(undefined);
  useEffect(() => {
    if (objectId === undefined) {
      setView(undefined);
      return;
    }
    const read = () => {
      const synced = (room.state as { objects?: SyncedObjects } | undefined)?.objects?.get(
        String(objectId),
      );
      const data = parseObjectData<{ required?: number; openUntil?: number }>(synced?.data);
      const next =
        data?.required === undefined
          ? undefined
          : {
              holders: synced?.occupants?.length ?? 0,
              required: data.required,
              open: data.openUntil !== undefined,
            };
      setView((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    read();
    room.onStateChange(read);
    return () => {
      room.onStateChange.remove(read);
    };
  }, [room, objectId]);
  if (!view) return null;
  const done = view.open ? view.required : Math.min(view.holders, view.required);
  return (
    <div className="hud-box coop-door-progress" role="status" aria-live="polite">
      <span>
        {view.open
          ? t('coopDoor.open')
          : t('coopDoor.holding', { count: done, total: view.required })}
      </span>
      <progress max={view.required} value={done} aria-hidden="true" />
    </div>
  );
}
